/**
 * MetricsTab — Cohort progress dashboard
 *
 * All roles see:
 *   1. At a Glance — enrolled, completed, avg completion, pending approvals
 *   2. Completion Distribution — stacked bar
 *
 * Admins also see:
 *   3. Mentor Coverage — active mentors, assigned/unassigned (when mentorsEnabled)
 *   4. Activity Completion Rates — table sorted by rate ascending
 */
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Tooltip from '@cloudscape-design/components/tooltip';
import { activitiesApi, ActivityProgressStat } from '../../api/activities';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorAlert } from '../../components/ErrorAlert';
import { StatCounter } from '../../components/StatCounter';
import { queryKeys } from '../../lib/queryClient';

interface ProgressTabProps {
  cohortId: string;
  mentorsEnabled: boolean;
  showActivityDetails?: boolean;
  onNavigateToTab?: (tabId: string) => void;
}

// ── Bar segment with Cloudscape Tooltip on hover ─────────────────────────────

interface BarSegmentProps {
  pct: number;
  color: string;
  label: string;
  count: number;
}

function BarSegment({ pct, color, label, count }: BarSegmentProps) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = React.useState(false);
  return (
    <span
      ref={ref}
      onPointerEnter={() => setVisible(true)}
      onPointerLeave={() => setVisible(false)}
      style={{ width: `${pct}%`, height: '100%', backgroundColor: color, transition: 'width 0.3s', display: 'block' }}
    >
      {visible && (
        <Tooltip
          content={`${label}: ${count} (${pct}%)`}
          getTrack={() => ref.current}
          onEscape={() => setVisible(false)}
          position="top"
        />
      )}
    </span>
  );
}

// ── Distribution bar ──────────────────────────────────────────────────────────

interface DistributionBarProps {
  notStarted: number;
  inProgress: number;
  completed: number;
  total: number;
}

function DistributionBar({ notStarted, inProgress, completed, total }: DistributionBarProps) {
  if (total === 0) return <Box color="text-body-secondary">No enrolled participants</Box>;

  const pctCompleted  = Math.round((completed  / total) * 100);
  const pctInProgress = Math.round((inProgress / total) * 100);
  const pctNotStarted = Math.max(0, 100 - pctCompleted - pctInProgress);

  const segments = [
    { pct: pctCompleted,  color: '#1d8102', label: 'Graduated',   count: completed  },
    { pct: pctInProgress, color: '#0972d3', label: 'In progress', count: inProgress },
    { pct: pctNotStarted, color: '#d5dbdb', label: 'Not started', count: notStarted },
  ];

  return (
    <SpaceBetween size="s">
      <div style={{ display: 'flex', height: '24px', borderRadius: '4px', overflow: 'hidden', width: '100%' }}>
        {segments.filter(s => s.pct > 0).map(s => (
          <BarSegment key={s.label} pct={s.pct} color={s.color} label={s.label} count={s.count} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {segments.map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: s.color, flexShrink: 0 }} />
            <Box fontSize="body-s">{s.label}: <strong>{s.count}</strong> ({s.pct}%)</Box>
          </div>
        ))}
      </div>
    </SpaceBetween>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ProgressTab({ cohortId, mentorsEnabled, showActivityDetails = false, onNavigateToTab }: ProgressTabProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.cohorts.progress(cohortId),
    queryFn: () => activitiesApi.getCohortProgress(cohortId),
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) return <LoadingSpinner message="Loading metrics..." />;
  if (error) return <ErrorAlert error={error} />;
  if (!data) return null;

  const { enrollment, mentorCoverage, activityStats } = data;
  const { totalEnrolled, totalGraduated, graduationRate, inProgress, notStarted } = enrollment;

  const mentorCoverageRate = totalEnrolled > 0
    ? Math.round((mentorCoverage.assignedMentees / totalEnrolled) * 100)
    : 0;

  const sortedActivities = [...activityStats].sort((a, b) => {
    const aHasWeek = a.week != null;
    const bHasWeek = b.week != null;
    // Unweekly activities first
    if (!aHasWeek && bHasWeek) return -1;
    if (aHasWeek && !bHasWeek) return 1;
    // Both weekly: sort by week, then order
    if (aHasWeek && bHasWeek) {
      if (a.week !== b.week) return (a.week ?? 0) - (b.week ?? 0);
    }
    // Both unweekly or same week: sort by order
    return (a.order ?? 0) - (b.order ?? 0);
  });

  return (
    <SpaceBetween size="l">

      {/* ── At a Glance ──────────────────────────────────────────────────── */}
      <Container header={<Header variant="h2">At a Glance</Header>}>
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
          <StatCounter label="Enrolled" value={totalEnrolled} />
          <StatCounter label="Graduated" value={totalGraduated} />
          <StatCounter label="Graduation Rate" value={`${graduationRate}%`} />
        </div>
      </Container>

      {/* ── Admin: Mentor Coverage ────────────────────────────────────────── */}
      {showActivityDetails && mentorsEnabled && (
        <Container header={<Header variant="h2">Mentor Coverage</Header>}>
          <SpaceBetween size="m">
            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              <StatCounter label="Mentors" value={mentorCoverage.activeMentors} />
              <StatCounter label="Participants Assigned" value={mentorCoverage.assignedMentees} />
              <StatCounter
                label="Unassigned"
                value={mentorCoverage.unassignedParticipants}
              />
              <StatCounter label="Coverage Rate" value={`${mentorCoverageRate}%`} />
            </div>
            {mentorCoverageRate === 100 ? (
              <Alert type="success">All participants have been assigned a mentor</Alert>
            ) : mentorCoverage.unassignedParticipants > 0 ? (
              <Alert
                type="warning"
                action={
                  onNavigateToTab && (
                    <Button onClick={() => onNavigateToTab('mentors')}>Go to Mentors</Button>
                  )
                }
              >
                {mentorCoverage.unassignedParticipants}{' '}
                {mentorCoverage.unassignedParticipants === 1 ? 'participant needs' : 'participants need'} a mentor assigned
              </Alert>
            ) : null}
          </SpaceBetween>
        </Container>
      )}

      <Container header={<Header variant="h2">Cohort Completion Status</Header>}>
        <DistributionBar
          notStarted={notStarted}
          inProgress={inProgress}
          completed={totalGraduated}
          total={totalEnrolled}
        />
      </Container>

      {/* ── Admin: Activity Completion Rates ─────────────────────────────── */}
      {showActivityDetails && (
        <Container header={
          <Header
            variant="h2"
            description="Activities listed in program order"
          >
            Activity Completion Rates
          </Header>
        }>
          {activityStats.length === 0 ? (
            <Box color="text-body-secondary">No activities have been created for this cohort yet.</Box>
          ) : (
            <Table
              variant="embedded"
              items={sortedActivities}
              columnDefinitions={[
                {
                  id: 'week',
                  header: 'Week',
                  cell: (item: ActivityProgressStat) => item.week ?? '—',
                  width: '6%',
                },
                {
                  id: 'title',
                  header: 'Activity',
                  cell: (item: ActivityProgressStat) => item.title,
                  width: '32%',
                },
                {
                  id: 'type',
                  header: 'Type',
                  cell: (item: ActivityProgressStat) => item.activityType,
                  width: '10%',
                },
                {
                  id: 'required',
                  header: 'Required?',
                  cell: (item: ActivityProgressStat) => (
                    item.requiredForGraduation ? <Badge color="blue">Required</Badge> : null
                  ),
                  width: '9%',
                },
                ...(mentorsEnabled ? [{
                  id: 'mentorApproval',
                  header: 'Review?',
                  cell: (item: ActivityProgressStat) => (
                    item.requiresMentorApproval ? <Badge>Required</Badge> : null
                  ),
                  width: '9%',
                }] : []),
                {
                  id: 'completions',
                  header: 'Completions',
                  cell: (item: ActivityProgressStat) => (
                    <span>
                      {item.completionCount}
                      {item.pendingCount > 0 && (
                        <span style={{ color: '#8d6605', marginLeft: '6px' }}>
                          +{item.pendingCount} pending
                        </span>
                      )}
                      <span style={{ color: '#5f6b7a', marginLeft: '4px' }}>
                        / {totalEnrolled}
                      </span>
                    </span>
                  ),
                  width: '10%',
                },
                {
                  id: 'rate',
                  header: 'Completion Rate',
                  cell: (item: ActivityProgressStat) => (
                    <ProgressBar
                      value={item.completionRate}
                      status={item.completionRate === 100 ? 'success' : 'in-progress'}
                      variant="standalone"
                      additionalInfo=""
                      description=""
                    />
                  ),
                  width: '24%',
                },
              ]}
              trackBy="activityId"
              empty={
                <Box textAlign="center" color="inherit">
                  <Box variant="p">No activities</Box>
                </Box>
              }
            />
          )}
        </Container>
      )}

    </SpaceBetween>
  );
}
